import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, RefreshCw, Layers, ExternalLink, CheckCircle2, Clock, AlertCircle, Copy, Box } from 'lucide-react';
import config from '../config';
import { URL_CONNEXION_B2B } from '../services/b2bApi';

export default function MerkleBatchesView() {
  const navigate = useNavigate();
  
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [copiedText, setCopiedText] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsRefreshing(true);
    try {
      // Session portail (cookie) ; la route interne reste appelée sans authentification.
      const [batchesRes, metricsRes] = await Promise.all([
        fetch(`${config.API_URL}/api/b2b/merkle/batches`, { credentials: 'include' }),
        fetch(`${config.API_URL}/internal/merkle/metrics`).catch(() => null) // Fails gracefully if not internal
      ]);

      if (batchesRes.ok) {
        const data = await batchesRes.json();
        setBatches(data.batches || []);
      } else if (batchesRes.status === 401) {
        navigate(URL_CONNEXION_B2B);
      }

      // We might be using internal endpoints, if not accessible we just ignore
      if (metricsRes && metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data);
      }
    } catch (err) {
      console.error("Error fetching batches", err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchBatchDetails = async (batchId) => {
    setSelectedBatch(batchId);
    setDetailsLoading(true);
    try {
      const res = await fetch(`${config.API_URL}/api/b2b/merkle/batches/${batchId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        setBatchDetails(await res.json());
      }
    } catch (err) {
      console.error(err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const copyToClipboard = (e, text, label) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1120] text-gray-100 font-sans p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex justify-between items-center mb-8 bg-[#162032] p-4 rounded-xl border border-gray-800">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/b2b/dashboard')}
              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-10 h-10 bg-teal-900/30 rounded-lg flex items-center justify-center">
              <Layers className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-teal-50">Batches Merkle (Sentinel V1)</h1>
              <p className="text-sm text-gray-400">Vue d'ensemble des ancrages groupés</p>
            </div>
          </div>
          <button 
            onClick={fetchData} 
            disabled={isRefreshing}
            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 text-gray-400 hover:text-white"
          >
            <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </header>

        {/* KPIs (if available via internal metrics or computed) */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-[#162032] p-6 rounded-xl border border-gray-800">
               <h2 className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">Total Batches</h2>
               <div className="text-3xl font-bold text-gray-100">{metrics.total_batches}</div>
            </div>
            <div className="bg-[#162032] p-6 rounded-xl border border-gray-800">
               <h2 className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">Batches Confirmés</h2>
               <div className="text-3xl font-bold text-teal-400">{metrics.confirmed_batches}</div>
            </div>
            <div className="bg-[#162032] p-6 rounded-xl border border-gray-800">
               <h2 className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">Taux d'Échec</h2>
               <div className="text-3xl font-bold text-red-400">{metrics.failure_rate_percent}%</div>
            </div>
            <div className="bg-[#162032] p-6 rounded-xl border border-gray-800">
               <h2 className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider">Temps Moyen (sec)</h2>
               <div className="text-3xl font-bold text-gray-100">{metrics.avg_confirmation_time_seconds}s</div>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6 h-[600px]">
          {/* Table Batches */}
          <div className={`bg-[#162032] rounded-xl border border-gray-800 overflow-hidden flex flex-col ${selectedBatch ? 'lg:w-2/3' : 'w-full'} transition-all`}>
            <div className="p-4 border-b border-gray-800 bg-[#1a2639]">
              <h2 className="text-lg font-medium text-gray-200">Liste des Batches</h2>
            </div>
            <div className="overflow-auto flex-1">
              <table className="w-full text-left border-collapse">
                <thead className="bg-[#0b1120]/80 text-xs uppercase text-gray-500 sticky top-0 backdrop-blur-sm z-10 border-b border-gray-800">
                  <tr>
                    <th className="px-6 py-4 font-medium">ID</th>
                    <th className="px-6 py-4 font-medium">Merkle Root</th>
                    <th className="px-6 py-4 font-medium">Preuves</th>
                    <th className="px-6 py-4 font-medium">Statut</th>
                    <th className="px-6 py-4 font-medium text-right">Créé le</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {batches.map(b => (
                    <tr 
                      key={b.id} 
                      onClick={() => fetchBatchDetails(b.id)}
                      className={`cursor-pointer transition-colors ${selectedBatch === b.id ? 'bg-teal-900/20 border-l-2 border-teal-500' : 'hover:bg-[#1c293f]'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">#{b.id}</td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-400">
                        {b.merkle_root ? `${b.merkle_root.substring(0, 10)}...${b.merkle_root.slice(-8)}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {b.proof_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {b.status === 'CONFIRMED' && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-[#1d3329] text-[#71c998]">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Confirmé
                          </span>
                        )}
                        {['BUILDING', 'QUEUED'].includes(b.status) && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-[#3b341f] text-[#d4b96a]">
                            <Clock className="w-3.5 h-3.5" /> {b.status}
                          </span>
                        )}
                        {b.status === 'FAILED' && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-[#3d2020] text-[#c96a6a]">
                            <AlertCircle className="w-3.5 h-3.5" /> Échoué
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-400">
                        {new Date(b.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Panneau Latéral Batch */}
          {selectedBatch && (
            <div className="bg-[#162032] rounded-xl border border-gray-800 lg:w-1/3 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-gray-800 bg-[#1a2639] flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-200">Détails du Batch #{selectedBatch}</h2>
                <button onClick={() => setSelectedBatch(null)} className="text-gray-500 hover:text-white">
                  <span className="sr-only">Fermer</span>
                  &times;
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1 bg-[#0b1120]">
                {detailsLoading ? (
                  <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div></div>
                ) : batchDetails ? (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Merkle Root</h3>
                      <div className="bg-[#162032] p-3 rounded-lg border border-gray-800 font-mono text-sm break-all text-gray-300 relative group">
                        {batchDetails.merkle_root}
                        <button 
                          onClick={(e) => copyToClipboard(e, batchDetails.merkle_root, 'root')}
                          className="absolute right-2 top-2 p-1.5 bg-gray-800 rounded-md opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-white"
                        >
                          {copiedText === 'root' ? <CheckCircle2 className="w-4 h-4 text-teal-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    {batchDetails.tx_hash && (
                      <div>
                        <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Transaction (Base Sepolia)</h3>
                        <a 
                          href={`https://sepolia.basescan.org/tx/${batchDetails.tx_hash}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between bg-[#162032] hover:bg-[#1a2639] p-3 rounded-lg border border-gray-800 font-mono text-sm text-teal-400 transition-colors"
                        >
                          <span className="truncate mr-4">{batchDetails.tx_hash}</span>
                          <ExternalLink className="w-4 h-4 flex-shrink-0" />
                        </a>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-[#162032] p-3 rounded-lg border border-gray-800">
                         <span className="text-xs text-gray-500 block mb-1">Preuves Incluses</span>
                         <span className="text-xl font-bold text-gray-200">{batchDetails.proof_count}</span>
                      </div>
                      <div className="bg-[#162032] p-3 rounded-lg border border-gray-800">
                         <span className="text-xs text-gray-500 block mb-1">Block Number</span>
                         <span className="text-xl font-bold text-gray-200">{batchDetails.block_number || '-'}</span>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Chronologie du Batch</h3>
                      <div className="space-y-4 relative before:absolute before:inset-0 before:ml-2.5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-800 before:to-transparent">
                         <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                            <div className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-[#0b1120] bg-blue-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10"></div>
                            <div className="w-[calc(100%-2.5rem)] md:w-[calc(50%-1.25rem)] bg-[#162032] p-3 rounded border border-gray-800">
                               <div className="flex justify-between mb-1">
                                  <div className="font-bold text-gray-200 text-sm">BUILDING</div>
                                  <div className="text-xs text-gray-500">{new Date(batchDetails.created_at).toLocaleTimeString()}</div>
                               </div>
                            </div>
                         </div>
                         {batchDetails.status === 'CONFIRMED' && (
                           <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                              <div className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-[#0b1120] bg-green-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10"></div>
                              <div className="w-[calc(100%-2.5rem)] md:w-[calc(50%-1.25rem)] bg-[#162032] p-3 rounded border border-gray-800">
                                 <div className="flex justify-between mb-1">
                                    <div className="font-bold text-gray-200 text-sm">CONFIRMED</div>
                                    <div className="text-xs text-gray-500">{new Date(batchDetails.confirmed_at).toLocaleTimeString()}</div>
                                 </div>
                              </div>
                           </div>
                         )}
                      </div>
                    </div>

                  </div>
                ) : (
                  <p className="text-gray-500 text-center">Sélectionnez un batch pour voir ses détails.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
